import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Filter, Search, ChevronDown, ChevronUp,
  DollarSign, TrendingUp, Users, Receipt, Wallet, Circle, RotateCcw, Save, Landmark, X,
} from 'lucide-react';
import { useAuth, api } from '../context/AuthContext';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const RANGE_PRESETS = [
  { key: 'all',        label: 'All time',     fn: () => ({ start: '', end: '' }) },
  { key: 'this_month', label: 'This month',   fn: () => {
      const d = new Date();
      return { start: new Date(d.getFullYear(), d.getMonth(), 1).toISOString(), end: '' };
  }},
  { key: 'last_month', label: 'Last month',   fn: () => {
      const d = new Date();
      return {
        start: new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString(),
        end:   new Date(d.getFullYear(), d.getMonth(), 0, 23, 59, 59).toISOString(),
      };
  }},
  { key: 'ytd',        label: 'Year to date', fn: () => {
      const d = new Date();
      return { start: new Date(d.getFullYear(), 0, 1).toISOString(), end: '' };
  }},
];

const SummaryCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className={`admin-summary-card ${accent || ''}`} data-testid={`summary-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="asc-icon"><Icon size={18} /></div>
    <div className="asc-body">
      <p className="asc-label">{label}</p>
      <p className="asc-value">{value}</p>
      {sub && <p className="asc-sub">{sub}</p>}
    </div>
  </div>
);

const UserBookingsDrawer = ({ userId, range, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (range.start) qs.set('start', range.start);
        if (range.end)   qs.set('end', range.end);
        setData(await api(`/api/admin/users/${userId}/bookings?${qs}`));
      } catch (e) { toast.error('Failed to load bookings', { description: e.message }); }
      setLoading(false);
    };
    load();
  }, [userId, range.start, range.end]);

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-drawer" onClick={(e) => e.stopPropagation()} data-testid="user-bookings-drawer">
        <div className="admin-drawer-header">
          <div>
            <h3>{data?.creator?.full_name || 'Loading…'}</h3>
            <p className="admin-drawer-sub">{data?.creator?.email}</p>
          </div>
          <button className="admin-btn ghost" onClick={onClose}>Close</button>
        </div>
        {loading ? (
          <p className="admin-drawer-empty">Loading…</p>
        ) : !data?.bookings?.length ? (
          <p className="admin-drawer-empty">No bookings in this range.</p>
        ) : (
          <table className="admin-inner-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Buyer</th>
                <th>Mins</th>
                <th>Status</th>
                <th>Gross</th>
                <th>Refund</th>
                <th>Net gross</th>
                <th>Tax</th>
                <th>Net owed</th>
                <th className="admin-col-split">Student paid</th>
                <th>Platform</th>
                <th>Stripe</th>
                <th>App store</th>
                <th>Platform net</th>
              </tr>
            </thead>
            <tbody>
              {data.bookings.map(b => (
                <tr key={b.id} className={!b.earned ? 'row-muted' : ''}>
                  <td>{b.created_at ? new Date(b.created_at).toLocaleDateString('en-AU') : ''}</td>
                  <td>{b.buyer_name || b.buyer_email || '—'}</td>
                  <td>{b.minutes}</td>
                  <td><span className={`admin-pill ${b.status}`}>{b.status}</span></td>
                  <td>{money(b.gross)}</td>
                  <td className={b.refund > 0 ? 'c-terracotta' : 'c-mute'}>{money(b.refund)}</td>
                  <td>{money(b.net_gross)}</td>
                  <td>{money(b.tax_withheld)}</td>
                  <td className="c-strong">{money(b.net_owed)}</td>
                  <td className="admin-col-split">{money(b.student_paid)}</td>
                  <td className="c-green">{money(b.platform_take)}</td>
                  <td>{money(b.stripe_fee)}</td>
                  <td data-testid={`drawer-app-store-${b.id}`}>{money(b.app_store_fee)}</td>
                  <td className="c-green c-strong">{money(b.platform_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Inline paid-to-date editor with dirty-tracked Save button.
const PaidToDateCell = ({ user, onSaved }) => {
  const initial = (user.paid_to_date ?? 0).toFixed(2);
  const [val, setVal] = useState(initial);
  const [saving, setSaving] = useState(false);
  const original = initial;
  const dirty = val !== original;

  useEffect(() => { setVal((user.paid_to_date ?? 0).toFixed(2)); }, [user.paid_to_date]);

  const save = async () => {
    const num = Number(val);
    if (!Number.isFinite(num) || num < 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      await api(`/api/admin/users/${user.id}/paid-to-date`, {
        method: 'PUT',
        body: JSON.stringify({ paid_to_date: num }),
      });
      toast.success('Paid-to-date saved', { description: `${user.full_name}: ${money(num)}` });
      onSaved();
    } catch (e) { toast.error('Save failed', { description: e.message }); }
    setSaving(false);
  };

  return (
    <div className="admin-paid-cell">
      <div className="admin-paid-input-group">
        <span className="admin-paid-currency">$</span>
        <input
          type="number" min="0" step="0.01"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          className="admin-paid-input"
          data-testid={`paid-input-${user.id}`}
        />
      </div>
      {dirty && (
        <button
          className="admin-paid-save"
          onClick={save}
          disabled={saving}
          data-testid={`paid-save-${user.id}`}
          title="Save"
        >
          {saving ? '…' : <Save size={13} />}
        </button>
      )}
      {dirty && !saving && (
        <button
          className="admin-paid-revert"
          onClick={() => setVal(original)}
          title="Revert"
          data-testid={`paid-revert-${user.id}`}
        >
          <RotateCcw size={12} />
        </button>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerId, setDrawerId] = useState(null);
  const [payoutUserId, setPayoutUserId] = useState(null);
  const [sortKey, setSortKey] = useState('remaining');
  const [sortDir, setSortDir] = useState('desc');

  const range = useMemo(() => {
    if (rangeKey === 'custom') {
      return {
        start: customStart ? new Date(customStart).toISOString() : '',
        end:   customEnd ? new Date(customEnd + 'T23:59:59').toISOString() : '',
      };
    }
    const preset = RANGE_PRESETS.find(r => r.key === rangeKey);
    return preset ? preset.fn() : { start: '', end: '' };
  }, [rangeKey, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (range.start) qs.set('start', range.start);
      if (range.end)   qs.set('end', range.end);
      const [sRes, uRes] = await Promise.all([
        api(`/api/admin/summary?${qs}`),
        api(`/api/admin/users?${qs}`),
      ]);
      setSummary(sRes.totals);
      setUsers(uRes.users || []);
    } catch (e) {
      toast.error('Failed to load dashboard', { description: e.message });
    }
    setLoading(false);
  }, [range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const updateTaxRate = async (u, pctValue) => {
    const rate = Math.max(0, Math.min(1, Number(pctValue) / 100));
    try {
      await api(`/api/admin/users/${u.id}/tax-rate`, {
        method: 'PUT',
        body: JSON.stringify({ tax_withholding_rate: rate }),
      });
      toast.success('Tax rate updated', { description: `${u.full_name}: ${(rate * 100).toFixed(1)}%` });
      load();
    } catch (e) { toast.error('Failed to update tax rate', { description: e.message }); }
  };

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = users.filter(u =>
      !q ||
      u.full_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q)
    );
    rows.sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return rows;
  }, [users, search, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const exportCSV = () => {
    const qs = new URLSearchParams();
    if (range.start) qs.set('start', range.start);
    if (range.end)   qs.set('end', range.end);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/admin/users/export.csv?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `6thmate_admin_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    }).catch(e => toast.error('Export failed', { description: e.message }));
  };

  if (!user) return null;
  if (!user.is_admin) return (
    <div className="admin-page" data-testid="admin-denied">
      <div className="admin-empty">
        <h2>Admin access required</h2>
        <p>Your account isn’t authorised to view the admin dashboard.</p>
        <button className="admin-btn primary" onClick={() => navigate('/')}>Back home</button>
      </div>
    </div>
  );

  const sortArrow = (k) => sortKey !== k ? null : (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />);

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="admin-header">
        <button className="admin-back" onClick={() => navigate('/')} data-testid="admin-back">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="admin-h1">Admin — Finance</h1>
          <p className="admin-sub">Bookings, refunds, tax withholding & manual payouts</p>
        </div>
        <button className="admin-btn primary export-btn" onClick={exportCSV} data-testid="admin-export">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="admin-range" data-testid="admin-range">
        <Filter size={14} />
        {RANGE_PRESETS.map(p => (
          <button
            key={p.key}
            className={`admin-range-btn ${rangeKey === p.key ? 'active' : ''}`}
            onClick={() => setRangeKey(p.key)}
            data-testid={`range-${p.key}`}
          >{p.label}</button>
        ))}
        <button
          className={`admin-range-btn ${rangeKey === 'custom' ? 'active' : ''}`}
          onClick={() => setRangeKey('custom')}
          data-testid="range-custom"
        >Custom</button>
        {rangeKey === 'custom' && (
          <>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} data-testid="range-start" />
            <span>→</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-testid="range-end" />
          </>
        )}
      </div>

      <p className="admin-section-label">Mentor payouts (based on the mentor's own rate)</p>
      <div className="admin-summary-grid" data-testid="admin-summary-mentor">
        <SummaryCard icon={TrendingUp} label="Mentor gross"  value={money(summary?.gross_revenue)} sub={`${summary?.bookings_count || 0} bookings`} />
        <SummaryCard icon={RotateCcw}  label="Refunds"       value={money(summary?.refunds)} sub="Mentor rate refunded" accent="accent-terracotta" />
        <SummaryCard icon={Wallet}     label="Net gross"     value={money(summary?.net_gross)} sub={`${summary?.creators_count || 0} mentors`} />
        <SummaryCard icon={Receipt}    label="Tax withheld"  value={money(summary?.tax_withheld)} sub="Sum of per-mentor rates" accent="accent-mist" />
        <SummaryCard icon={Wallet}     label="Net owed"      value={money(summary?.net_owed)} sub="Net gross − tax" />
        <SummaryCard icon={Users}      label="Paid out"      value={money(summary?.paid_out)} sub="Manual total" />
        <SummaryCard icon={Circle}     label="Remaining"     value={money(summary?.remaining)} sub="Still to pay mentors" accent="accent-terracotta" />
      </div>
      <p className="admin-section-label">Platform (paid by students on top of the mentor rate)</p>
      <div className="admin-summary-grid" data-testid="admin-summary-platform">
        <SummaryCard icon={TrendingUp} label="Student paid"  value={money(summary?.student_paid)} sub={`${money(summary?.student_refunds)} refunded`} />
        <SummaryCard icon={DollarSign} label="Platform / my profit" value={money(summary?.platform_take)} sub="15% platform fee" accent="accent-green" />
        <SummaryCard icon={Receipt}    label="Stripe fees"   value={money(summary?.stripe_fees)} sub="1.75% + $0.30 / txn" accent="accent-terracotta" />
        <SummaryCard icon={Receipt}    label="App store fees" value={money(summary?.app_store_fees)} sub="15% Google / Apple" accent="accent-terracotta" />
        <SummaryCard icon={Wallet}     label="Platform net"  value={money(summary?.platform_net)} sub="Profit + Stripe + app store" accent="accent-green" />
      </div>

      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={16} />
          <input
            type="text" placeholder="Search mentors by name or email…"
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="admin-search"
          />
        </div>
        <span className="admin-count">{filteredSorted.length} mentor{filteredSorted.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div className="admin-empty">Loading dashboard…</div>
      ) : filteredSorted.length === 0 ? (
        <div className="admin-empty">No mentors match your filters.</div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-users-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('full_name')}>Mentor {sortArrow('full_name')}</th>
                <th onClick={() => toggleSort('bookings_count')}>Bookings {sortArrow('bookings_count')}</th>
                <th onClick={() => toggleSort('gross_revenue')}>Gross {sortArrow('gross_revenue')}</th>
                <th onClick={() => toggleSort('refunds')}>Refunds {sortArrow('refunds')}</th>
                <th onClick={() => toggleSort('net_gross')}>Net gross {sortArrow('net_gross')}</th>
                <th>Tax rate %</th>
                <th onClick={() => toggleSort('tax_withheld')}>Tax withheld {sortArrow('tax_withheld')}</th>
                <th onClick={() => toggleSort('net_owed')}>Net owed {sortArrow('net_owed')}</th>
                <th>Paid to date</th>
                <th onClick={() => toggleSort('remaining')}>Remaining {sortArrow('remaining')}</th>
                <th className="admin-col-split" onClick={() => toggleSort('student_paid')}>Student paid {sortArrow('student_paid')}</th>
                <th onClick={() => toggleSort('platform_take')}>Platform / profit (15%) {sortArrow('platform_take')}</th>
                <th onClick={() => toggleSort('stripe_fees')}>Stripe fees {sortArrow('stripe_fees')}</th>
                <th onClick={() => toggleSort('app_store_fees')}>App store (15%) {sortArrow('app_store_fees')}</th>
                <th onClick={() => toggleSort('platform_net')}>Platform net {sortArrow('platform_net')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map(u => (
                <tr key={u.id} data-testid={`admin-row-${u.id}`}>
                  <td>
                    <div className="admin-user-cell">
                      <div className="admin-avatar">{(u.full_name || '?')[0]}</div>
                      <div>
                        <p className="admin-user-name">{u.full_name || '—'}</p>
                        <p className="admin-user-sub">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td>{u.bookings_count}</td>
                  <td>{money(u.gross_revenue)}</td>
                  <td className={u.refunds > 0 ? 'c-terracotta c-strong' : 'c-mute'}>{money(u.refunds)}</td>
                  <td>{money(u.net_gross)}</td>
                  <td>
                    <input
                      type="number" min="0" max="100" step="0.5"
                      className="admin-tax-input"
                      defaultValue={(u.tax_withholding_rate * 100).toFixed(1)}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v) && Math.abs(v/100 - u.tax_withholding_rate) > 0.001) {
                          updateTaxRate(u, v);
                        }
                      }}
                      data-testid={`tax-input-${u.id}`}
                    />%
                  </td>
                  <td className="c-mute">{money(u.tax_withheld)}</td>
                  <td className="c-strong">{money(u.net_owed)}</td>
                  <td>
                    <PaidToDateCell user={u} onSaved={load} />
                  </td>
                  <td className={u.remaining > 0 ? 'c-terracotta c-strong' : 'c-mute'}>{money(u.remaining)}</td>
                  <td className="admin-col-split">{money(u.student_paid)}</td>
                  <td className="c-green">{money(u.platform_take)}</td>
                  <td className={u.stripe_fees > 0 ? 'c-terracotta' : 'c-mute'}>{money(u.stripe_fees)}</td>
                  <td className={u.app_store_fees > 0 ? 'c-terracotta' : 'c-mute'} data-testid={`admin-app-store-${u.id}`}>{money(u.app_store_fees)}</td>
                  <td className="c-green c-strong" data-testid={`admin-platform-net-${u.id}`}>{money(u.platform_net)}</td>
                  <td>
                    <button
                      className="admin-btn ghost small"
                      onClick={() => setDrawerId(u.id)}
                      data-testid={`view-bookings-${u.id}`}
                    >View</button>
                    <button
                      className="admin-btn ghost small"
                      onClick={() => setPayoutUserId(u.id)}
                      data-testid={`payout-${u.id}`}
                      style={{ marginLeft: 4 }}
                    ><Landmark size={12} /> Payout</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerId && (
        <UserBookingsDrawer
          userId={drawerId}
          range={range}
          onClose={() => setDrawerId(null)}
        />
      )}

      {payoutUserId && (
        <PayoutDetailsModal
          userId={payoutUserId}
          onClose={() => setPayoutUserId(null)}
        />
      )}
    </div>
  );
};

const PayoutDetailsModal = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await api(`/api/admin/payout-details/${userId}`);
        setData(d);
      } catch (e) {
        toast.error(e.message || 'Load failed');
      }
      setLoading(false);
    })();
  }, [userId]);

  const copy = (label, val) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  };

  return (
    <div
      onClick={onClose}
      data-testid="payout-details-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 460, width: '100%', position: 'relative' }}
      >
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>
          <X size={22} />
        </button>
        <h2 style={{ margin: '0 0 12px', fontFamily: 'Fraunces, serif', fontSize: 22, color: '#111' }}>Payout details</h2>
        {loading ? (
          <p style={{ color: '#6B7280' }}>Loading…</p>
        ) : !data?.has_payout ? (
          <p style={{ color: '#6B7280' }}>This mentor has not entered cashout details yet.</p>
        ) : (
          <div>
            <p style={{ margin: '4px 0', color: '#4B5563' }}><strong>Mentor:</strong> {data.user?.full_name} <span style={{ color: '#9CA3AF' }}>· {data.user?.email}</span></p>
            <div style={{ background: '#FEF3C7', padding: '12px 14px', borderRadius: 10, margin: '12px 0', color: '#78350F' }}>
              <strong>Balance to pay:</strong> ${(data.balance_held || 0).toFixed(2)} AUD
            </div>
            <p style={{ margin: '8px 0 4px', color: '#374151' }}><strong>Account holder</strong></p>
            <p onClick={() => copy('Account holder', data.account_holder_name)} data-testid="payout-detail-holder" style={{ margin: 0, padding: '10px 12px', background: '#F3F4F6', borderRadius: 8, fontFamily: 'monospace', cursor: 'pointer' }}>{data.account_holder_name}</p>
            <p style={{ margin: '8px 0 4px', color: '#374151' }}><strong>BSB</strong></p>
            <p onClick={() => copy('BSB', data.bsb)} data-testid="payout-detail-bsb" style={{ margin: 0, padding: '10px 12px', background: '#F3F4F6', borderRadius: 8, fontFamily: 'monospace', cursor: 'pointer' }}>{data.bsb}</p>
            <p style={{ margin: '8px 0 4px', color: '#374151' }}><strong>Account number</strong></p>
            <p onClick={() => copy('Account number', data.account_number)} data-testid="payout-detail-account" style={{ margin: 0, padding: '10px 12px', background: '#F3F4F6', borderRadius: 8, fontFamily: 'monospace', cursor: 'pointer' }}>{data.account_number || data.account_number_masked}</p>
            <p style={{ fontSize: 12, color: '#6B7280', marginTop: 12 }}>Tap any row to copy. Updated {data.updated_at ? new Date(data.updated_at).toLocaleString('en-AU') : '—'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
